"use client"
import React, { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import DashboardLayout from "../../components/layout/DashboardLayout"
import Button from "../../components/common/Button"
import Thumbnail from "../../components/common/Thumbnail"
import VideoShowPlayer from "../../components/common/VideoShowPlayer"
import StatusUpdatePopup from "../../components/common/StatusUpdatePopup"
import { ArrowLeft, CheckCircle, ChevronDown, DollarSign, Edit2, Eye, Percent, Play, Share2, Users, Image } from "lucide-react"
import { getPackageById, updatePackage } from "../../api/packageService"
import { API_COURSE_URL } from '../../utils/constants';
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"

const PackageDetails: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [activeTab, setActiveTab] = useState("Overview")
  const [packageData, setPackageData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showShareModal, setShowShareModal] = useState(false)
  const [showDescriptionModal, setShowDescriptionModal] = useState(false)
  const [showVideoPlayer, setShowVideoPlayer] = useState(false)
  const [mediaType, setMediaType] = useState<'image' | 'video' | 'unknown'>('image')
  const [showStatusPopup, setShowStatusPopup] = useState(false)
  const [, setIsUpdatingStatus] = useState(false)
  
  const tabs = ["Overview", "Coupons", "Affiliate", "Members list", "Manage"]
  
  useEffect(() => {
    const fetchPackage = async () => {
      try {
        setLoading(true)
        
        if (!id) {
          setError("Package ID is required")
          return
        }
        
        console.log("Fetching package with ID:", id)
        const response = await getPackageById(id)
        console.log("Package API Response:", response)
        
        if (response.status === 200 && response.data) {
          const packageDetails = response.data
          
          console.log("Package details received:", packageDetails)
          console.log("Banner base64 length:", packageDetails.bannerBase64?.length)
          console.log("Banner URL:", packageDetails.bannerUrl)
          console.log("Theme base64 length:", packageDetails.themeBase64?.length)
          console.log("Theme URL:", packageDetails.themeUrl)
          console.log("Status from API:", packageDetails.status) // Added log for status
          
          // Process banner/theme images with token
          const authToken = localStorage.getItem("AUTH_TOKEN")
          let bannerImage = ""
          let themeImage = ""
          
          // Handle banner image (base64 or URL)
          if (packageDetails.bannerBase64) {
            // Clean base64 string and add proper data URI prefix
            const rawBase64 = packageDetails.bannerBase64
            console.log("Raw base64 (first 100 chars):", rawBase64.substring(0, 100))
            
            // Remove any existing data URI prefix
            const cleanBase64 = rawBase64.replace(/^data:image\/[a-z]+;base64,/, '')
            bannerImage = `data:image/jpeg;base64,${cleanBase64}`
            
            console.log("Processed banner base64 (first 100 chars):", bannerImage.substring(0, 100))
            console.log("Banner base64 length:", bannerImage.length)
          } else if (packageDetails.bannerUrl && packageDetails.bannerUrl.startsWith('/uploads/')) {
            bannerImage = `${API_COURSE_URL}${packageDetails.bannerUrl}?token=${authToken}`
            console.log("Using banner URL with token:", bannerImage)
          } else if (packageDetails.bannerUrl) {
            bannerImage = packageDetails.bannerUrl
            console.log("Using direct banner URL:", bannerImage)
          }
          
          // Handle theme image (base64 or URL)
          if (packageDetails.themeBase64) {
            // Clean base64 string and add proper data URI prefix
            const cleanBase64 = packageDetails.themeBase64.replace(/^data:image\/[a-z]+;base64,/, '')
            themeImage = `data:image/jpeg;base64,${cleanBase64}`
            console.log("Using theme base64 image")
          } else if (packageDetails.themeUrl && packageDetails.themeUrl.startsWith('/uploads/')) {
            themeImage = `${API_COURSE_URL}${packageDetails.themeUrl}?token=${authToken}`
            console.log("Using theme URL with token:", themeImage)
          } else if (packageDetails.themeUrl) {
            themeImage = packageDetails.themeUrl
            console.log("Using direct theme URL:", themeImage)
          }
          
          // Format course details
          const formattedCourses = packageDetails.courseDetails?.map((course: any) => ({
            id: course._id,
            title: course.title,
            status: course.publishStatus || "Draft"
          })) || []
          
          setMediaType('image')
          setPackageData({
            id: packageDetails._id,
            title: packageDetails.title,
            description: packageDetails.description,
            instructor: "Package Creator", // Default as no instructor info in API
            thumbnail: bannerImage || themeImage, // Use banner first, then theme as fallback
            bannerUrl: bannerImage,
            themeUrl: themeImage,
            videoUrl: "",
            // Use the actual status from API instead of deriving from isDraft
            status: packageDetails.status || (packageDetails.isDraft ? "Draft" : "Published"),
            price: `₹${packageDetails.price || 0}`,
            priceDetails: packageDetails.priceDetails || [],
            courses: formattedCourses,
            faqs: packageDetails.faqs || [],
            telegramId: packageDetails.telegramId || [],
            metrics: {
              totalEarnings: "₹0", // Default as no metrics in API
              purchasedUsers: 0,
              packageCompleted: "0%",
              avgPurchaseRate: "0%",
            }
          })
          
          // console.log("Final status used:", packageDetails.status || (packageDetails.isDraft ? "Draft" : "Published"))
          // console.log("Final thumbnail src:", bannerImage || themeImage)
        } else {
          setError(response.message || "Failed to load package details")
        }
      } catch (err) {
        console.error("Error fetching package:", err)
        setError("Please check your authentication and try again")
      } finally {
        setLoading(false)
      }
    }
    
    fetchPackage()
  }, [id])
  
  // Get auth token for URLs
  const authToken = localStorage.getItem("AUTH_TOKEN")
  const videoSrc = packageData?.videoUrl ? 
    (packageData.videoUrl.startsWith('http') ? `${packageData.videoUrl}?token=${authToken}` : packageData.videoUrl) 
    : ""
    
  const thumbnailSrc = packageData?.thumbnail || packageData?.bannerUrl || ""
  
  //console.log("Current thumbnail source:", thumbnailSrc)
  
  // Function to handle closing video player
  const handleCloseVideoPlayer = () => {
    setShowVideoPlayer(false)
  }
  
  // Function to get status color classes
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Draft": return "text-blue-600 bg-blue-50"
      case "Published": return "text-green-600 bg-green-50"
      case "Sold out": return "text-yellow-600 bg-yellow-50"
      case "Selling": return "text-purple-600 bg-purple-50"
      default: return "text-gray-600 bg-gray-50"
    }
  }
  
  // Function to handle status update
  const handleStatusUpdate = async (packageId: string, newStatus: string) => {
    setIsUpdatingStatus(true);
    try {
      // Determine isDraft based on the selected status
      const isDraft = newStatus === "Draft";
      
      // Call the updatePackage API with only the necessary parameters
      const response = await updatePackage({
        packageId,
        status: newStatus,
        isDraft
      });

      if (response.status === 200) {
        // Update the local state with the new status
        setPackageData((prev: any) => ({ 
          ...prev, 
          status: newStatus
        }));
        alert('Package status updated successfully!');
      } else {
        alert(response.message || 'Failed to update package status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('An error occurred while updating the status');
    } finally {
      setIsUpdatingStatus(false);
      setShowStatusPopup(false);
    }
  };
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
        </div>
      </DashboardLayout>
    )
  }
  
  if (error) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-red-500">{error}</p>
          <Button 
            onClick={() => navigate("/package")} 
            className="mt-4"
          >
            Back to packages
          </Button>
        </div>
      </DashboardLayout>
    )
  }
  
  if (!packageData) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-gray-500">Package not found.</p>
          <Button 
            onClick={() => navigate("/package")} 
            className="mt-4"
          >
            Back to packages
          </Button>
        </div>
      </DashboardLayout>
    )
  }
  
  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Top Bar */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-200">
          <button 
            onClick={() => navigate("/package")} 
            className="flex items-center text-gray-800 hover:text-gray-900 text-sm mt-4 ml-2"
          >
            <ArrowLeft className="mr-2" size={18} />
            Back to Packages
          </button>
        </div>
        
        {/* Package Header with Enhanced Thumbnail/Video */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            {/* Enhanced Media section */}
            <div className="w-full lg:w-2/5">
              <div className="relative aspect-[16/9] group">
                {showVideoPlayer && videoSrc ? (
                  <div className="relative w-full h-full">
                    <VideoShowPlayer 
                      src={videoSrc} 
                      onClose={handleCloseVideoPlayer}
                      autoPlay={true}
                    />
                  </div>
                ) : mediaType === 'image' ? (
                  <div className="w-full h-full relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-blue-600/10 z-10"></div>
                    <img
                      src={thumbnailSrc}
                      alt={packageData.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        console.log("Image failed to load:", thumbnailSrc)
                        const target = e.target as HTMLImageElement
                        target.src = "https://via.placeholder.com/400x300/6366f1/white?text=Package+Image"
                      }}
                      onLoad={() => {
                        //console.log("Image loaded successfully:", thumbnailSrc)
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent z-20"></div>
                  </div>
                ) : mediaType === 'video' ? (
                  <div className="relative w-full h-full">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-blue-600/10 z-10"></div>
                    <Thumbnail 
                      src={thumbnailSrc}
                      alt={packageData.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent z-20"></div>
                    <button 
                      onClick={() => setShowVideoPlayer(true)}
                      className="absolute inset-0 flex items-center justify-center z-30 group/play"
                    >
                      <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 sm:p-4 shadow-xl transition-all duration-300 group-hover/play:bg-white group-hover/play:scale-110">
                        <Play className="text-purple-600 ml-0.5 sm:ml-1" size={24} />
                      </div>
                    </button>
                  </div>
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center p-4">
                    <div className="bg-white rounded-full p-4 sm:p-6 shadow-lg mb-1">
                      <Image className="text-gray-400" size={32} />
                    </div>
                    <span className="text-gray-500 font-medium text-center text-sm">No thumbnail available</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Enhanced Package info section */}
            <div className="flex-1 p-4 sm:p-5 lg:p-4">
              <div className="flex flex-col sm:flex-row justify-between items-start mb-3 gap-3">
                <div className="flex-1">
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight mb-1">{packageData.title}</h1>
                  <p className="text-gray-600 font-medium text-sm">by {packageData.instructor}</p>
                </div>
                {/* Enhanced Status badge */}
                <button 
                  onClick={() => setShowStatusPopup(true)}
                  className={`inline-flex items-center px-3 py-2 rounded-full text-xs font-semibold border-2 shadow-md transition-all duration-300 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${getStatusColor(packageData.status)} hover:bg-opacity-90 cursor-pointer whitespace-nowrap`}
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-current mr-1 opacity-75"></div>
                  {packageData.status}
                  <ChevronDown className="ml-1 transition-transform duration-200" size={14} />
                </button>
              </div>
              
<div className="text-gray-700 leading-relaxed mb-4">

  <div className="package-description-collapsed text-sm">
    <ReactMarkdown remarkPlugins={[remarkGfm]}>
      {packageData.description || "No description available."}
    </ReactMarkdown>
  </div>

  {packageData.description &&
    packageData.description.trim().split(/\s+/).length > 30 && (
      <button
        type="button"
        onClick={() => setShowDescriptionModal(true)}
        className="text-purple-600 hover:text-purple-700 font-semibold mt-2 inline-flex items-center transition-colors duration-200 text-xs"
      >
        Read more

      </button>
    )}

</div>          
              
              {/* Enhanced Action buttons */}
              <div className="flex flex-wrap items-center gap-2">
                <button 
                  className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-lg shadow-md hover:from-blue-600 hover:to-blue-700 hover:shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500/30 text-xs"
                  onClick={() => setShowShareModal(true)}
                >
                  <Share2 className="mr-1" size={14} />
                  <span className="hidden xs:inline">Share</span>
                  <span className="xs:hidden">Share</span>
                </button>
                <button 
                  className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold rounded-lg shadow-md hover:from-emerald-600 hover:to-emerald-700 hover:shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 text-xs"
                  onClick={() => window.open(`/packages/landing/${packageData.id}`, '_blank')}
                >
                  <Eye className="mr-1" size={14} />
                  <span className="hidden xs:inline">View landing</span>
                  <span className="xs:hidden">View</span>
                </button>
                <button 
                  className="inline-flex items-center px-3 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-semibold rounded-lg shadow-md hover:from-purple-600 hover:to-purple-700 hover:shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500/30 text-xs"
                  onClick={() => navigate(`/packages/edit/${packageData.id}`)}
                >
                  <Edit2 className="mr-1" size={14} />
                  <span className="hidden xs:inline">Edit</span>
                  <span className="xs:hidden">Edit</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Enhanced Tabs */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-1">
          <div className="flex flex-wrap gap-1">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-300 ${
                  activeTab === tab 
                    ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-md" 
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
        
        {/* Tab Content */}
        {activeTab === "Overview" && (
          <div className="space-y-6">
            {/* Enhanced Overview Metrics */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl shadow p-3 text-center">
                <div className="bg-emerald-500 rounded-full p-2 w-fit mx-auto mb-2">
                  <DollarSign className="text-white" size={16} />
                </div>
                <p className="text-base font-bold text-emerald-800 mb-1">{packageData.metrics.totalEarnings}</p>
                <p className="text-xs font-medium text-emerald-700">Total earnings</p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl shadow p-3 text-center">
                <div className="bg-blue-500 rounded-full p-2 w-fit mx-auto mb-2">
                  <Users className="text-white" size={16} />
                </div>
                <p className="text-base font-bold text-blue-800 mb-1">{packageData.metrics.purchasedUsers}</p>
                <p className="text-xs font-medium text-blue-700">Purchased users</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl shadow p-3 text-center">
                <div className="bg-purple-500 rounded-full p-2 w-fit mx-auto mb-2">
                  <CheckCircle className="text-white" size={16} />
                </div>
                <p className="text-base font-bold text-purple-800 mb-1">{packageData.metrics.packageCompleted}</p>
                <p className="text-xs font-medium text-purple-700">Package completed</p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-xl shadow p-3 text-center">
                <div className="bg-orange-500 rounded-full p-2 w-fit mx-auto mb-2">
                  <Percent className="text-white" size={16} />
                </div>
                <p className="text-base font-bold text-orange-800 mb-1">{packageData.metrics.avgPurchaseRate}</p>
                <p className="text-xs font-medium text-orange-700">Average Purchase Rate</p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Status Update Popup */}
      <StatusUpdatePopup
        isOpen={showStatusPopup}
        onClose={() => setShowStatusPopup(false)}
        currentStatus={packageData.status}
        courseId={packageData.id}
        onUpdate={handleStatusUpdate}
      />
      
      {/* Enhanced Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-xl p-4 w-full max-w-md shadow-xl transform transition-all duration-300 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">Share Your Package</h3>
                <p className="text-gray-600 text-xs">Share the active payment links with your audiences</p>
              </div>
              <button 
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-1 hover:bg-gray-100 rounded-full"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-gray-800 mb-1">💳 Payment link</label>
                <div className="flex rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                  <input
                    type="text"
                    value={`${window.location.origin}/payment/package/${packageData.id}`}
                    readOnly
                    className="flex-1 px-2 py-2 text-xs bg-gray-50 border-0 focus:outline-none"
                  />
                  <button 
                    className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 text-xs font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:ring-offset-1"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/payment/package/${packageData.id}`);
                      // Add a subtle feedback here if needed
                    }}
                  >
                    Copy
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-gray-800 mb-1">🌐 Landing page link</label>
                <div className="flex rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                  <input
                    type="text"
                    value={`${window.location.origin}/packages/landing/${packageData.id}`}
                    readOnly
                    className="flex-1 px-2 py-2 text-xs bg-gray-50 border-0 focus:outline-none"
                  />
                  <button 
                    className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-3 text-xs font-medium hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:ring-offset-1"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/packages/landing/${packageData.id}`);
                      // Add a subtle feedback here if needed
                    }}
                  >
                    Copy
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-gray-800 mb-1">🛒 Checkout page link</label>
                <div className="flex rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                  <input
                    type="text"
                    value={`${window.location.origin}/checkout/package/${packageData.id}`}
                    readOnly
                    className="flex-1 px-2 py-2 text-xs bg-gray-50 border-0 focus:outline-none"
                  />
                  <button 
                    className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-3 text-xs font-medium hover:from-purple-600 hover:to-purple-700 transition-all duration-200 focus:outline-none focus:ring-1 focus:ring-purple-500 focus:ring-offset-1"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/checkout/package/${packageData.id}`);
                      // Add a subtle feedback here if needed
                    }}
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <Button 
                onClick={() => setShowShareModal(false)}
                className="px-3 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-semibold transition-colors duration-200 text-xs"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

{/* Full Package Description Modal */}
{showDescriptionModal && (
 <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
  <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] shadow-2xl flex flex-col -translate-y-8">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">

        <div className="min-w-0">
          <h4 className="text-md font-bold text-gray-900">
            {packageData.title}
          </h4>      
        </div>

        <button
          type="button"
          onClick={() => setShowDescriptionModal(false)}
          className="text-gray-400 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
          aria-label="Close description"
        >
          <span className="text-xl leading-none">×</span>
        </button>

      </div>

      {/* Description */}
      <div className="px-6 py-5 overflow-y-auto">

        <div className="text-gray-700 leading-relaxed text-sm">

        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {packageData.description || "No description available."}
        </ReactMarkdown>

      </div>

      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-200 flex justify-end flex-shrink-0">

        <button
          type="button"
          onClick={() => setShowDescriptionModal(false)}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors"
        >
          Close
        </button>

      </div>

    </div>

  </div>
)}      
    </DashboardLayout>
  )
}

export default PackageDetails